import { useEffect, useState } from "react";
import axios from "axios";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HistoricalChart = ({ coin }) => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistoricalData = async () => {
      try {
        const response = await axios.get(`${API}/historical/${coin}?days=30`);
        const data = response.data;
        
        const formattedData = data.prices.map((price, index) => {
          const date = new Date(data.timestamps[index]);
          return {
            date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            price: price,
            timestamp: data.timestamps[index]
          };
        });
        
        setChartData(formattedData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching historical data:", error);
        setLoading(false);
      }
    };

    fetchHistoricalData();
  }, [coin]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="glass-card rounded-lg p-3 border border-white/20">
          <p className="text-xs text-muted-foreground mb-1">{payload[0].payload.date}</p>
          <p className="text-sm font-bold text-data">
            ${payload[0].value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="glass-card rounded-xl p-6 h-64 flex items-center justify-center" data-testid={`chart-loading-${coin.toLowerCase()}`}>
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          <p className="mt-2 text-xs text-muted-foreground">Loading chart...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card rounded-xl p-6 border-white/10 hover:border-primary/50 transition-colors duration-300 animate-fade-in-up" data-testid={`chart-${coin.toLowerCase()}`}>
      <div className="mb-4">
        <h3 className="text-lg font-semibold heading-font">{coin} Price History</h3>
        <p className="text-xs text-muted-foreground">Last 30 Days</p>
      </div>
      
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={chartData}>
          <defs>
            <linearGradient id={`colorPrice${coin}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
          <XAxis 
            dataKey="date" 
            stroke="#666" 
            style={{ fontSize: '10px' }}
            tick={{ fill: '#888' }}
          />
          <YAxis 
            stroke="#666" 
            style={{ fontSize: '10px', fontFamily: 'JetBrains Mono' }}
            tick={{ fill: '#888' }}
            tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area 
            type="monotone" 
            dataKey="price" 
            stroke="#3B82F6" 
            strokeWidth={2}
            fill={`url(#colorPrice${coin})`}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default HistoricalChart;